import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, AlertTriangle, CheckCircle2, Users } from "lucide-react";

/**
 * FilterPanel for Shipment Costing Page
 * Compact single-row layout with inline filters - readable fonts
 */
const FilterPanel = ({
  // Filter states
  filterErrors,
  setFilterErrors,
  filterSuccessOnly,
  setFilterSuccessOnly,
  filterErrorType,
  setFilterErrorType,
  filterService,
  setFilterService,
  filterCountry,
  setFilterCountry,
  filterClient,
  setFilterClient,
  filterCostMin,
  setFilterCostMin,
  filterDateFrom,
  setFilterDateFrom,
  filterDateTo,
  setFilterDateTo,
  filterDpClientsOnly,
  setFilterDpClientsOnly,
  // Data for dropdowns
  uniqueErrorTypesWithCounts = [],
  uniqueServices = [],
  uniqueCountries = [],
  uniqueClients = [],
  dpClientCodes = [],
  allLedgerClients = [], // NEW: All clients from ledgers collection
  // Computed values
  filteredOrdersCount,
  totalOrdersCount,
  dpClientOrdersCount,
  hasActiveFilters,
  clearFilters
}) => {
  // Combine unique clients from loaded data with ALL clients from ledgers
  // This ensures new clients always appear in dropdown even if no orders loaded yet
  const combinedClients = [...new Set([
    ...uniqueClients,
    ...allLedgerClients
  ])].sort();

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 shadow-sm" data-testid="filter-panel">
      <div className="flex flex-wrap items-center gap-3">
        {/* DP CLIENTS Only toggle - NEW */}
        <Button
          variant={filterDpClientsOnly ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterDpClientsOnly && setFilterDpClientsOnly(!filterDpClientsOnly)}
          className={`h-9 text-sm px-3 ${filterDpClientsOnly ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'}`}
          title={`Show only DP CLIENT orders (${dpClientOrdersCount || 0} orders)`}
        >
          <Users className="w-4 h-4 mr-1.5" />
          DP Clients {dpClientOrdersCount ? `(${dpClientOrdersCount})` : ''}
        </Button>

        <div className="w-px h-6 bg-slate-300" />

        {/* Status toggles */}
        <Button
          variant={filterErrors ? "default" : "outline"}
          size="sm"
          onClick={() => {
            setFilterErrors(!filterErrors);
            if (!filterErrors) setFilterSuccessOnly(false);
          }}
          className={`h-9 text-sm px-3 ${filterErrors ? 'bg-red-600 hover:bg-red-700' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" />
          Errors
        </Button>
        
        <Button
          variant={filterSuccessOnly ? "default" : "outline"}
          size="sm"
          onClick={() => {
            setFilterSuccessOnly(!filterSuccessOnly);
            if (!filterSuccessOnly) setFilterErrors(false);
          }}
          className={`h-9 text-sm px-3 ${filterSuccessOnly ? 'bg-green-600 hover:bg-green-700' : 'border-green-200 text-green-600 hover:bg-green-50'}`}
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" />
          Success
        </Button>

        <div className="w-px h-6 bg-slate-300" />
        
        {/* Dropdown filters */}
        <select
          value={filterErrorType}
          onChange={(e) => setFilterErrorType(e.target.value)}
          className={`h-9 px-3 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 ${filterErrorType ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'}`}
        >
          <option value="">Error Type</option>
          {uniqueErrorTypesWithCounts.map(({ type, count }) => (
            <option key={type} value={type}>{type} ({count})</option>
          ))}
        </select>
        
        <select
          value={filterService}
          onChange={(e) => setFilterService(e.target.value)}
          className={`h-9 px-3 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 ${filterService ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-white'}`}
        >
          <option value="">Service</option>
          {uniqueServices.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        
        <select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          className={`h-9 px-3 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 ${filterCountry ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-white'}`}
        >
          <option value="">Country</option>
          {uniqueCountries.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        
        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className={`h-9 px-3 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-500 ${filterClient ? 'border-orange-400 bg-orange-50' : 'border-slate-300 bg-white'}`}
        >
          <option value="">Client</option>
          {combinedClients.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        
        <Input
          type="number"
          value={filterCostMin}
          onChange={(e) => setFilterCostMin(e.target.value)}
          placeholder="Min ₹"
          className={`h-9 w-24 text-sm ${filterCostMin ? 'border-purple-400 bg-purple-50' : ''}`}
        />

        {/* Clear & Results */}
        {hasActiveFilters && (
          <>
            <div className="w-px h-6 bg-slate-300" />
            <span className="text-sm text-slate-600">
              <span className="font-semibold text-purple-600">{filteredOrdersCount}</span>/{totalOrdersCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-9 text-sm px-3 text-red-600 hover:text-red-800 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;
